<template>
    <template v-if="filterType === 'text'">
        <InputText v-model="filterModel.value"
                   type="text"
                   name="filterInputText"
                   @keydown.enter="filterCallback()"
                   :pt="{
                    root: {
                        style: { 'width': '100%', 'height': '34px', 'font-size': '14px', 'color': 'black', 'font-weight': 'normal', ...filterStyle },
                        class: ['w-full rounded-md border pl-8 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black-500/50', filterClass]
                    },
                }" />
    </template>

    <template v-else-if="filterType === 'range'">
        <div style="min-width: 40px;">
            <!-- <div style="min-width:50px;"> -->
            <DatePicker v-model="filterModel.value"
                        selectionMode="range"
                        dateFormat="yy-mm-dd"
                        style="font-weight:normal;width: 100%;"
                        :manualInput="false"
                        :unstyled="false"
                        @filter="filterCallback()"
                        :pt="{
                            input: {
                                style: { 'width': '100%', 'font-size': '14px', ...filterStyle },
                                class: filterClass
                            }
                        }" />
        </div>
    </template>

    <template v-else-if="filterType === 'date'">
        <div style="min-width: 40px;">
            <DatePicker v-model="filterModel.value"
                        selectionMode="single"
                        dateFormat="yy-mm-dd"
                        :manualInput="false"
                        :unstyled="false"
                        @filter="filterCallback()"
                        :pt="{
                            input: {
                                style: { 'width': '100%', 'font-size': '14px', 'color': 'green', ...filterStyle },
                                class: filterClass
                            }
                        }" />
        </div>
    </template>

    <template v-else-if="filterType === 'value'">
        <InputNumber v-model="filterModel.value"
                     :format="false"
                     @filter="filterCallback()"
                     :pt="{
                        root: {
                            style: { 'width': '100%', ...filterStyle },
                            class: filterClass
                        },
                    }" />
    </template>

    <template v-else-if="filterType === 'checkbox'">
        <Checkbox v-model="filterModel.value"
                  binary
                  @filter="filterCallback()"
                  :pt="{
                    root: {
                        style: { 'padding': '0', 'margin': '0', ...filterStyle },
                        class: filterClass
                    },
                    filterinput: {
                        style: {
                            'padding': '0', 'margin': '0'
                        },
                    },
                    input: {
                        style: {
                            'max-width': '25px',
                        },
                    }
                }" />
    </template>

    <template v-else-if="filterType === 'autocomplete'">
        <AutoComplete v-model="filterModel.value"
                      @filter="filterCallback()"
                      :suggestions="selectOptions"
                      @complete="searchItems"
                      :virtualScrollerOptions="{ itemSize: 38 }"
                      multiple
                      optionLabel="label"
                      dataKey="value"
                      dropdown
                      dropdownMode="current"
                      forceSelection
                      name="filterInputAutocomplete"
                      :delay="50"
                      :emptySelectionMessage="__('No data')"
                      :pt="autoCompletePt"
                      panelClass="omnia-ac-panel" />
    </template>

    <template v-else-if="filterType === 'dropdown'">
        <Select v-model="filterModel.value"
                @filter="filterCallback()"
                :options="selectOptions"
                :virtualScrollerOptions="{ itemSize: 38 }"
                multiple
                optionLabel="label"
                optionValue="value"
                name="filterInputSelect"
                :style="filterStyle"
                :class="filterClass"
                :unstyled="false"
                :pt="{
                    root: {
                        class: '!bg-white !w-full !rounded-md !border !text-sm !focus:outline-none !focus:ring-2 !focus:ring-black-500/50',
                        style: { 'font-weight': 'normal' }
                    }
                }"
                :emptySelectionMessage="__('No data')" />
    </template>

</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import DatePicker from 'primevue/datepicker';
import AutoComplete from 'primevue/autocomplete'
import Checkbox from 'primevue/checkbox'
import Select from 'primevue/select'
import { __ } from '@helper/utils.js'

const props = defineProps({
    filterModel: Object,
    filterCallback: Function,
    filterOptions: String,
    filterParams: Object,
    filterStyle: Object,
    filterClass: String,
    filterTokenStyle: Object,
    filterTokenClass: String,
    filterListStyle: Object,
    filterListClass: String,
    filterItemStyle: Object,
    filterItemClass: String,
    filterInputStyle: Object,
    filterInputClass: String,
    filterPanelStyle: Object,
    filterPanelClass: String,

    filterType: {
        type: String,
        default: 'text',
    }
})

const autoCompletePt = {
    root: {
        class: 'relative w-full',
        style: { width: '100%' },
    },

    pcInputText: {
        root: {
            style: { width: '100%', height: '34px', fontSize: '14px', color: 'black', ...(props.filterStyle ?? {}) },
            class: [
                'w-full rounded-md border pl-8 pr-10 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black-500/50',
                props.filterClass
            ]
        }
    },

    inputMultiple: {
        class: [
            'w-full min-h-[34px] rounded-md border bg-white px-3 py-1.5 text-sm',
            'flex flex-wrap items-center gap-1',
            'focus-within:outline-none focus-within:ring-2 focus-within:ring-black-500/50',
            props.filterClass
        ],
        style: { width: '100%', fontSize: '14px', color: 'black', ...(props.filterStyle ?? {}) }
    },
    inputChip: {
        class: 'flex-1 min-w-[3rem]'
    },
    input: {
        class: ['flex-1 min-w-[3rem] bg-transparent outline-none border-0 p-0 m-0', props.filterInputClass],
        style: { width: '100%', lineHeight: '1.25rem', height: '1.25rem', ...(props.filterInputStyle ?? {}) }
    },

    chipItem: {
        class: 'mr-1 mb-1'
    },
    pcChip: {
        root: {
            class: [
                'inline-flex items-center rounded-md bg-gray-100 text-gray-800 px-2 py-0.5 text-xs font-medium',
                props.filterTokenClass
            ],
            style: { ...(props.filterTokenStyle ?? {}) }
        },
        label: { class: 'leading-4' },
        removeIcon: { class: 'ml-1 text-gray-400 hover:text-gray-600 cursor-pointer' }
    },

    dropdown: {
        class:
            'absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center text-gray-500 hover:text-gray-700'
    },
    dropdownIcon: {
        class: 'text-gray-500'
    },

    overlay: {
        class: 'mt-1 rounded-md border border-gray-200 bg-white shadow-lg omnia-ac-overlay ' + props.filterPanelClass,
        style: {                          // <-- dodaj styl panelu tutaj
            ...(props.filterPanelStyle ?? {})
        }
    },
    listContainer: {
        class: 'max-h-64 overflow-auto',
        style: { ...(props.filterListStyle ?? {}) }
    },
    list: {
        class: 'py-1'
    },
    option: ({ context }) => ({
        class: [
            'px-3 py-2 text-sm cursor-pointer select-none flex items-center gap-2',
            context?.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50',
            context?.focused ? 'bg-gray-50' : '',
            context?.selected ? 'bg-primary-50 text-blue-700' : 'text-gray-700',
            props.filterItemClass
        ],
        style: props.filterItemStyle ?? undefined
    }),
    emptyMessage: {
        class: 'px-3 py-2 text-sm text-gray-500',
        style: { ...(props.filterListStyle ?? {}) }
    }
}

// --------------------------------------------------------------------------------------

const selectOptions = ref([{}])
const searchItems = async (event) => {
    await axios.post(route("helper-select", { select: [props.filterOptions], filter: event?.query, params: props.filterParams || {} }))
        .then((response) => {
            selectOptions.value = response.data[props.filterOptions];
        });
}

// --------------------------------------------------------------------------------------

onMounted(async () => {
    if (!props.filterModel) {
        return null;
    }

    if (props.filterType === 'dropdown') {
        searchItems({ query: '' })
    }
})

// --------------------------------------------------------------------------------------

</script>

<style scoped>
.p-column-filter .p-column-filter-menu-button.p-link {
    margin-left: 2px !important;
    padding: 0 !important;
    margin: 0 !important;
    width: 20px;
    color: green !important;
}

.p-column-filter .p-column-filter-menu-button.p-column-filter-menu-button-active {
    margin-left: 2px !important;
    border-radius: 5px !important;
    background-color: firebrick;
    color: white !important;
}

.p-column-filter .p-column-filter-clear-button.p-link {
    padding: 0 !important;
    margin: 0 !important;
    width: 20px;
    color: #FEC107 !important;
}

.p-column-filter .p-column-filter-element .p-inputnumber .p-inputtext {
    font-size: 12px;
}

.p-virtualscroller {
    contain: content;
}

.p-autocomplete-items {
    position: relative;
}

.p-virtualscroller-content {
    position: relative;
}
</style>

<style>
.omnia-ac-panel {
    /* Domyślna szerokość startowa, zostanie nadpisana przez JS */
    min-width: 250px;
    max-width: 90vw !important;
    /* Zabezpieczenie przed wyjściem poza ekran */
}
</style>
